import React from 'react'
import { useLayoutEffect, useRef } from 'react';
import * as am4maps from '@amcharts/amcharts4/maps';
import * as am4core from '@amcharts/amcharts4/core';
import am4geodata_worldLow from '@amcharts/amcharts4-geodata/worldLow';
import themeColors from '../../theme/colors';

const styles = {
    chartDiv: () => ({
        width: "100%",
        height: 500,
    })
}

const AmMapChart = ({
    data,
    amchartId,
    styleProps,
    renderedCharts
}) => {
    const chart = useRef(null)
    const excludedCountries = ["AQ"]

    useLayoutEffect(() => {
        // renderedCharts.current[amchartId] = false
        let x = am4core.create(`chartdiv-map-${amchartId}`, am4maps.MapChart)

        // low quality map definition
        x.geodata = am4geodata_worldLow;
        // set projection
        x.projection = new am4maps.projections.Miller();

        // zoom out btn
        let zoomOut = x.tooltipContainer.createChild(am4core.ZoomOutButton);
        zoomOut.align = "right";
        zoomOut.valign = "top";
        zoomOut.margin(20, 20, 20, 20);
        zoomOut.events.on("hit", () => {
            x.goHome();
            zoomOut.hide();
        });
        zoomOut.hide();

        // Create map polygon series
        let polygonSeries = x.series.push(new am4maps.MapPolygonSeries());
        polygonSeries.exclude = excludedCountries;
        polygonSeries.useGeodata = true;
        polygonSeries.nonScalingStroke = true;
        polygonSeries.strokeWidth = 0.5;
        polygonSeries.calculateVisualCenter = true;



        // change color based on value
        polygonSeries.heatRules.push({
            property: "fill",
            target: polygonSeries.mapPolygons.template,
            min: am4core.color(styleProps.color).brighten(1),
            max: am4core.color(styleProps.color).brighten(-0.3),
            logarithmic: true
        });

        // set tooltip to 0 if no data
        polygonSeries.tooltip.label.adapter.add("text", function (text, target) {
            if (!target.dataItem.value) return "{name}: [bold]0[/]";
            return "{name}: [bold]{value}[/]";
        });
        let labelSeries = x.series.push(new am4maps.MapImageSeries());
        // labelSeries.data = data // getSeries(resultSet)
        labelSeries.dataFields.value = "value";
        labelSeries.events.on("beforedatavalidated", () => {
            labelSeries.data = data
        })
        labelSeries.id = "markers"

        const imageTemplate = labelSeries.mapImages.template;
        imageTemplate.nonScaling = true

        imageTemplate.adapter.add("latitude", function (latitude, target) {
            const polygon = polygonSeries.getPolygonById(target.dataItem.dataContext.id);
            if (polygon) {
                return polygon.visualLatitude;
            }
            return latitude;
        })

        imageTemplate.adapter.add("longitude", function (longitude, target) {
            const polygon = polygonSeries.getPolygonById(target.dataItem.dataContext.id);
            if (polygon) {
                return polygon.visualLongitude;
            }
            return longitude;
        })

        polygonSeries.data = data // getSeries(resultSet)
        polygonSeries.cursorTooltipEnabled = false


        let label = labelSeries.mapImages.template.createChild(am4core.Label);
        label.text = "{id}: {value}"
        label.padding(0, 0, 0, 0);
        label.horizontalCenter = "middle";
        label.verticalCenter = "middle";
        label.fontSize = 10;
        label.fontWeight = '900';
        label.interactionsEnabled = false;
        label.stroke = am4core.color('#fff')
        label.strokeOpacity = 0.5

        // heat legend
        let heatLegend = x.createChild(am4maps.HeatLegend);
        heatLegend.id = "heatLegend";
        heatLegend.series = polygonSeries;
        heatLegend.align = "left";
        heatLegend.valign = "top";
        heatLegend.width = am4core.percent(20);
        heatLegend.background.fill = am4core.color("#000");
        heatLegend.background.fillOpacity = 0.05;
        heatLegend.orientation = 'vertical';
        // heatLegend.markerCount = 3;
        heatLegend.padding(5, 5, 5, 5);
        heatLegend.margin(15, 0, 0, 5);

        // zoom in on country when clicked
        let polygonTemplate = polygonSeries.mapPolygons.template;
        polygonTemplate.nonScalingStroke = true;
        polygonTemplate.strokeWidth = 0.5;
        polygonTemplate.events.on("hit", function (ev) {
            ev.target.series.chart.zoomToMapObject(ev.target);
            zoomOut.show();
        });
        polygonTemplate.events.on('over', (e) => {
            if (!isNaN(e.target.dataItem.value)) {
                heatLegend.valueAxis.showTooltipAt(e.target.dataItem.value)
            }
            else {
                heatLegend.valueAxis.hideTooltip();
            }
        })
        // show zoom out button if zoomed in with mousewheel
        x.events.on('wheelup', () => {
            zoomOut.show();
        })
        polygonTemplate.tooltipText = " ";
        let hs = polygonTemplate.states.create("hover");
        hs.properties.fill = am4core.color(styleProps.color);

        // Set up custom heat map legend labels using axis ranges
        let minRange = heatLegend.valueAxis.axisRanges.create();
        minRange.label.horizontalCenter = "left";

        let middleRange = heatLegend.valueAxis.axisRanges.create();
        middleRange.label.horizontalCenter = "middle";

        let maxRange = heatLegend.valueAxis.axisRanges.create();
        maxRange.label.horizontalCenter = "right";

        // Blank out internal heat legend value axis labels
        heatLegend.valueAxis.renderer.labels.template.adapter.add("text", () => "");

        // Update heat legend value labels
        polygonSeries.events.on("datavalidated", function (ev) {
            let heatLegend = ev.target.map.getKey("heatLegend");

            let min = heatLegend.series.dataItem.values.value.low;
            let minRange = heatLegend.valueAxis.axisRanges.getIndex(0);
            minRange.value = min;
            minRange.label.text = "" + heatLegend.numberFormatter.format(min);

            let max = heatLegend.series.dataItem.values.value.high;
            let maxRange = heatLegend.valueAxis.axisRanges.getIndex(2);
            maxRange.value = max;
            maxRange.label.text = "" + heatLegend.numberFormatter.format(max);

            let middle = Math.round(min + (max - min) / 2)
            let middleRange = heatLegend.valueAxis.axisRanges.getIndex(1)
            middleRange.value = middle
            middleRange.label.text = '' + heatLegend.numberFormatter.format(middle)
        });

        x.logo.disabled = true

        x.events.on("ready", () => {
            renderedCharts.current[amchartId] = true
            // console.log('MAP RENDERED', renderedCharts)
        })

        chart.current = x;

        return () => {
            x.dispose();
        };
    }, []);

    return (
        <div id={`chartdiv-map-${amchartId}`} style={styles.chartDiv()} />
    )
}

AmMapChart.defaultProps = {
    color: themeColors.FII_BAR_CHART
}

export default AmMapChart;